'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import CartIcon from '@/components/icons/CartIcon';
import { Button } from '@/components';
import { useCartOptional } from '@/components/contexts/CartContext';
import type { MerchTileProduct, VariantOption } from '@/components/sections/DankLifestyleSection';
import { PRODUCT_PLACEHOLDER_IMAGE } from '@shared/lib/product-placeholder';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

function ensureAbsoluteImageUrl(uri: string | null | undefined) {
  if (!uri) return null;
  if (uri.startsWith('http://') || uri.startsWith('https://')) return uri;
  const clean = uri.startsWith('/') ? uri : `/${uri}`;
  return `${API_BASE}${clean}`;
}

type Props = {
  product: MerchTileProduct;
};

/** Product card UI from `DankLifestyleSection` merch grid (image, hover variant pills, title, price, cart). */
export default function DankLifestyleMerchProductTile({ product }: Props) {
  const router = useRouter();
  const cart = useCartOptional();
  const addItem = cart?.addItem;
  const openCart = cart?.openCart;

  const [hovered, setHovered] = useState(false);
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(null);
  const [productColor, setProductColor] = useState<string | null>(() => product.colors[0] ?? null);
  const [productSize, setProductSize] = useState<string | null>(() => {
    if (product.sizes.length === 0) return null;
    if (product.colors.length > 0) {
      return (
        product.options.find((opt) => opt.color === product.colors[0])?.size || product.sizes[0] || null
      );
    }
    return product.sizes[0] ?? null;
  });

  useEffect(() => {
    setProductColor(product.colors[0] ?? null);
    const firstSize =
      product.sizes.length > 0
        ? product.colors.length > 0
          ? product.options.find((opt) => opt.color === product.colors[0])?.size || product.sizes[0] || null
          : product.sizes[0] ?? null
        : null;
    setProductSize(firstSize);
    setSelectedOptionKey(null);
  }, [product.id]);

  const finalSelected = useMemo((): VariantOption | null => {
    let sel: VariantOption | null = product.options.length > 0 ? product.options[0] : null;
    if ((productColor || productSize) && product.options.length > 0) {
      let filtered = product.options;
      if (productColor && product.colors.length > 0) {
        filtered = filtered.filter((opt) => opt.color === productColor);
      }
      if (productSize && product.sizes.length > 0 && filtered.length > 0) {
        filtered = filtered.filter((opt) => opt.size === productSize);
      }
      if (filtered.length > 0) {
        sel = filtered[0];
      } else if (productColor && product.colors.length > 0) {
        const colorOptions = product.options.filter((opt) => opt.color === productColor);
        if (colorOptions.length > 0) sel = colorOptions[0];
      }
    }
    if (selectedOptionKey) {
      const keySelected = product.options.find((o) => o.key === selectedOptionKey);
      if (keySelected) sel = keySelected;
    }
    return sel;
  }, [product, productColor, productSize, selectedOptionKey]);

  const displayPrice = finalSelected?.price ?? product.basePrice;
  const displayImage = finalSelected?.heroShotUri
    ? ensureAbsoluteImageUrl(finalSelected.heroShotUri) || product.baseImage
    : product.baseImage;

  return (
    <div
      className="overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white transition-shadow"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex flex-col">
        <div className="group relative aspect-square w-full overflow-hidden bg-neutral-5">
          <img
            src={displayImage}
            alt={product.title}
            className="absolute inset-0 h-full w-full object-cover object-top"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              if (!el.src.includes('/media/brand/logo.webp')) {
                el.src = PRODUCT_PLACEHOLDER_IMAGE;
                el.className = 'absolute inset-0 h-full w-full object-contain opacity-30';
              }
            }}
          />

          {hovered && (product.colors.length > 1 || product.sizes.length > 1) && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-[rgba(23,22,90,0.5)] to-[rgba(52,59,145,0.2)] opacity-70 transition-opacity" />
              <div className="absolute right-3 bottom-3 left-3 flex flex-col gap-2">
                {product.colors.length > 1 && (
                  <div className="flex flex-wrap gap-1.5">
                    {product.colors.map((color) => {
                      const isSelected = productColor === color;
                      return (
                        <Button
                          key={color}
                          type="button"
                          variant={isSelected ? 'solid' : 'bordered'}
                          color={isSelected ? 'white' : 'white'}
                          onClick={(e) => {
                            e.preventDefault();
                            setProductColor(color);
                            const colorOptions = product.options.filter((opt) => opt.color === color);
                            if (colorOptions.length > 0 && !productSize) {
                              setProductSize(colorOptions[0].size || null);
                            }
                          }}
                          className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                            isSelected
                              ? 'border-neutral-70 bg-neutral-70 text-constant-white'
                              : 'border-neutral-10 bg-constant-white text-neutral-70'
                          }`}
                          title={color}
                        >
                          {color}
                        </Button>
                      );
                    })}
                  </div>
                )}

                {product.sizes.length > 1 && (
                  <div className="scrollbar-hide w-full overflow-x-auto overflow-y-hidden rounded-lg border border-neutral-10 bg-constant-white shadow-lg">
                    <div className="flex min-w-full w-max gap-1.5 px-1.5 py-1">
                      {(() => {
                        const filteredByColor =
                          productColor && product.colors.length > 1
                            ? product.options.filter((opt) => opt.color === productColor)
                            : product.options;

                        const uniqueSizes = Array.from(
                          new Set(
                            filteredByColor.map((opt) => opt.size).filter((size): size is string => size !== null)
                          )
                        ).sort((a, b) => {
                          const aNum = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
                          const bNum = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
                          if (aNum !== 0 || bNum !== 0) {
                            return aNum - bNum;
                          }
                          const sizeOrder = [
                            'xs',
                            's',
                            'm',
                            'l',
                            'xl',
                            'xxl',
                            'xxxl',
                            '2xl',
                            '3xl',
                            '4xl',
                            'small',
                            'medium',
                            'large',
                            'extra small',
                            'extra large',
                            'one size',
                          ];
                          const aIndex = sizeOrder.findIndex((size) => a.toLowerCase().includes(size));
                          const bIndex = sizeOrder.findIndex((size) => b.toLowerCase().includes(size));
                          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                          if (aIndex !== -1) return -1;
                          if (bIndex !== -1) return 1;
                          return a.localeCompare(b);
                        });

                        const totalSizes = uniqueSizes.length;

                        return uniqueSizes
                          .map((size) => {
                            const opt = filteredByColor.find((o) => o.size === size);
                            if (!opt) return null;

                            const isSelected = productSize === size;
                            return (
                              <Button
                                key={size}
                                type="button"
                                variant={isSelected ? 'solid' : 'bordered'}
                                color="white"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setProductSize(size);
                                }}
                                className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                                  isSelected
                                    ? 'bg-neutral-70 text-constant-white'
                                    : 'bg-constant-white text-neutral-70'
                                } ${totalSizes === 1 ? 'w-full' : 'min-w-[92px] shrink-0 flex-1'}`}
                                title={size || 'Default'}
                              >
                                {size}
                              </Button>
                            );
                          })
                          .filter(Boolean);
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {hovered && product.colors.length <= 1 && product.sizes.length <= 1 && product.options.length > 0 && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-[rgba(23,22,90,0.5)] to-[rgba(52,59,145,0.2)] opacity-70 transition-opacity" />
              <div className="absolute right-3 bottom-3 left-3">
                <div className="scrollbar-hide w-full overflow-x-auto overflow-y-hidden rounded-lg border border-neutral-10 bg-constant-white shadow-lg">
                  <div className="flex min-w-full w-max gap-1.5 px-1.5 py-1">
                    {product.options.map((opt) => {
                      const isSelected = selectedOptionKey === opt.key;
                      return (
                        <Button
                          key={opt.key}
                          type="button"
                          variant={isSelected ? 'solid' : 'bordered'}
                          color="white"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedOptionKey(opt.key);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                            isSelected
                              ? 'bg-neutral-70 text-constant-white'
                              : 'bg-constant-white text-neutral-70'
                          } ${product.options.length === 1 ? 'w-full' : 'min-w-[92px] shrink-0 flex-1'}`}
                          title={opt.label}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between p-4">
          <div className="max-w-64 min-w-0 flex flex-1 flex-col gap-0.5">
            <Link href={product.href}>
              <h3 className="overflow-hidden text-lg font-medium text-ellipsis whitespace-nowrap text-text-primary transition-colors">
                {product.title}
              </h3>
            </Link>

            <p className="text-xl font-bold text-text-primary">${Number(displayPrice || 0).toFixed(2)}</p>
          </div>

          <Button
            type="button"
            variant="icon"
            color="white"
            size="icon-md"
            className="rounded-xl p-3"
            aria-label={finalSelected ? 'Add to cart' : 'Select options'}
            title={finalSelected ? 'Add to cart' : 'Select options'}
            onClick={() => {
              if (!finalSelected?.productSlug || !finalSelected?.actualPackageId) {
                router.push(product.href);
                return;
              }
              if (addItem) {
                addItem({
                  productId: finalSelected.productId,
                  name: product.title,
                  catalogItemId: finalSelected.productId,
                  packageId: finalSelected.productSlug,
                  price: finalSelected.price,
                  actualPackageId: finalSelected.actualPackageId,
                } as any);
                openCart?.();
              } else {
                router.push(product.href);
              }
            }}
          >
            <CartIcon size={24} color="white" className="h-6 w-6 text-constant-white" />
          </Button>
        </div>
      </div>
    </div>
  );
}
